import React, {useEffect, useState} from 'react'
import {FormControlLabel, Checkbox, Radio, FormControl} from "@mui/material";
import TextArea from "./elements/TextArea";
import SelectTemplateView from "../pages/Contracts/SelectTemplateView";
import {httpClient} from "../funcs";
import notifyUser, {notifyAboutError} from "../methods/notifyUser";
import {ReactComponent as CheckboxIcon} from "../pages/Icons/size24/Checkbox.svg";
import {ReactComponent as CheckboxChecked} from "../pages/Icons/size24/CheckboxChecked.svg";
import {ReactComponent as RadioChecked} from "../pages/Icons/size24/RadioChecked.svg";
import {ReactComponent as RadioUnchecked} from "../pages/Icons/size24/RadioUnchecked.svg";


export enum IntegrationType {
    noIntegration = 0,
    useOneTemplate = 1,
    useManyTemplate = 2
}

type Props = {
    serviceName: string
    placeholderText?: string
    integrationConfig: any
    integrationFieldName: string
    idFieldName: string
    $store: any
    $commitToStore: Function
    onSave?: () => void
}

export default function TemplateIntegrationSettings({
    serviceName,
    placeholderText,
    integrationConfig,
    idFieldName,
    $store,
    $commitToStore,
    onSave
}: Props) {
    const [integrationType, setIntegrationType] = useState<number>(
        integrationConfig?.integration_type ?? 0
    )
    const [oneTemplate, setOneTemplate] = useState<any>(integrationConfig?.one_template ?? null)
    const [manyTemplates, setManyTemplates] = useState<Map<string, string>>(makeManyTemplatesIds())
    const [useActualUserCard, setUseActualUserCard] = useState<boolean>(
        integrationConfig?.use_actual_user_card ?? false
    )
    const [templates, setTemplates] = useState<any[]>([])
    const defaultPlaceholder = placeholderText || `Введите номер лота из ${serviceName}`

    function makeManyTemplatesIds() {
        let dict = new Map<string, string>()
        if (integrationConfig?.many_templates) {
            for (const id of integrationConfig.many_templates) {
                dict.set(id.id, id[idFieldName])
            }
        }
        return dict
    }

    useEffect(() => {
        loadTemplates()
    }, [])

    useEffect(() => {
        save()
    }, [integrationType, oneTemplate, manyTemplates, useActualUserCard])

    async function loadTemplates() {
        try {
            const {data} = await httpClient.get(`/templates`, {params: {limit: 40}})
            setTemplates(data)
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    async function save() {
        if (integrationType == 1 && !oneTemplate) {
            notifyUser({ $store, $commitToStore }, "Выберите шаблон", "Укажите шаблон, на основе которого будут создаваться договоры")
            return
        }
        try {
            await httpClient.patch("/user/self", {
                realty_integration: {
                    'integration_type': integrationType,
                    'one_template': oneTemplate,
                    'many_templates': Array.from(manyTemplates.entries()).map((pairs) => {
                        return {'id': pairs[0], [idFieldName]: pairs[1]}
                    }),
                    'use_actual_user_card': useActualUserCard,
                }
            })

            await httpClient.get('/user/self').then(({ data }) => {
                $commitToStore({
                    userSession: data
                })
            })

            if (onSave) {
                onSave()
            }
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        }
    }

    function makeOneTemplateSelector() {
        return <FormControl sx={{width: "100%", marginTop: "15px"}}>
            <SelectTemplateView
                selectedTemplate={oneTemplate}
                didSelectTemplate={(value: any) => {
                    setOneTemplate(value)
                }}
            />
        </FormControl>
    }

    function makeManyTemplatesListView() {
        return <div>
            {
                templates.map((template) => {
                    return <div key={template._id} style={{marginTop: "15px"}}>
                        <div className="buttonsContainer commonLabel">
                            {template.name + '. ' + (template.comment ?? '')}
                        </div>
                        <div style={{marginTop: "10px"}}>
                            <TextArea
                                value={manyTemplates.get(template._id) || ''}
                                placeholder={defaultPlaceholder}
                                onChange={({target: {value}}: any) => {
                                    const newManyTemplates = new Map(manyTemplates)
                                    newManyTemplates.set(template._id, value)
                                    setManyTemplates(newManyTemplates)
                                }}
                            />
                        </div>
                    </div>
                })
            }
        </div>
    }

    return (
        <div>
            <div
                className="buttonsContainer"
                style={{marginLeft: "-10px", marginTop: "15px"}}
            >
                <Radio
                    checked={integrationType === IntegrationType.noIntegration}
                    onChange={() => setIntegrationType(IntegrationType.noIntegration)}
                    name="radio-buttons"
                    inputProps={{ 'aria-label': 'A' }}
                    checkedIcon={<RadioChecked />}
                    icon={<RadioUnchecked />}
                />
                <div className="commonLabel">
                    Интеграция отключена
                </div>
            </div>
            <div
                className="buttonsContainer"
                style={{marginLeft: "-10px", marginTop: "15px"}}
            >
                <Radio
                    checked={integrationType === IntegrationType.useOneTemplate}
                    onChange={() => setIntegrationType(IntegrationType.useOneTemplate)}
                    name="radio-buttons"
                    inputProps={{ 'aria-label': 'A' }}
                    checkedIcon={<RadioChecked />}
                    icon={<RadioUnchecked />}
                />
                <div className="commonLabel">
                    У меня один шаблон на все квартиры
                </div>
            </div>
            {
                integrationType === IntegrationType.useOneTemplate &&
                makeOneTemplateSelector()
            }
            <div
                className="buttonsContainer"
                style={{marginLeft: "-10px", marginTop: "15px"}}
            >
                <Radio
                    checked={integrationType === IntegrationType.useManyTemplate}
                    onChange={() => setIntegrationType(IntegrationType.useManyTemplate)}
                    name="radio-buttons"
                    inputProps={{ 'aria-label': 'A' }}
                    checkedIcon={<RadioChecked />}
                    icon={<RadioUnchecked />}
                />
                <div className="commonLabel">
                    У меня отдельный шаблон на каждую квартиру
                </div>
            </div>
            {
                integrationType === IntegrationType.useManyTemplate &&
                makeManyTemplatesListView()
            }

            {
                (integrationType === IntegrationType.useOneTemplate || integrationType === IntegrationType.useManyTemplate) &&
                <div>
                    <hr className="newHR"/>
                    <div className="commonLabel" style={{marginTop: "15px", marginBottom: "10px"}}>
                        Дополнительные настройки
                    </div>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                icon={<CheckboxIcon className="icon"/>}
                                checkedIcon={<CheckboxChecked className="icon"/>}
                                checked={useActualUserCard}
                                onChange={(e) => setUseActualUserCard(e.target.checked)}
                            />
                        }
                        label={
                            <div className="Body_15">
                                Использовать дополнительные карточки подписанта
                                <div className="commonSubtitle" style={{marginTop: "5px"}}>
                                    Если включено, при создании договора будут использоваться данные из дополнительных карточек подписанта, если они настроены в шаблоне
                                </div>
                            </div>
                        }
                    />
                </div>
            }
        </div>
    )
}
